import type { Product } from "./types";

// These imported families do not all share an English slug stem. Keep this
// list aligned with the curated groups in the admin catalog detail endpoint.
const curatedFamilies = [
  ["mini-pumpkin-tea-light-lantern", "mini-pumpkin-tea-light-lantern-leaf-stand"],
  ["dummy-13-articulated-action-figure", "dummy-13-action-figure-20cm", "dummy-13-action-figure-30cm-large"],
  ["articulated-shark-keychain", "mini-shark-articulated-keychain"],
  ["simple-phone-stand", "small-smartphone-stand-print-in-place"],
  ["cat-shelf-hook-lantern-black", "cat-shelf-hook-lantern-brown", "cat-shelf-hook-lantern-surprise", "قلاب-دیواری-گربه-با-فانوس-آویز-سفید-7a352ed4"],
  ["articulated-octopus-figure", "articulated-octopus-figure-multicolor"],
  ["mini-ghost-tea-light-lantern", "mini-ghost-tea-light-lantern-petg-bw", "mini-ghost-tea-light-lantern-multicolor"],
  ["lantern-corps-ring-green", "lantern-corps-ring-blue", "lantern-corps-ring-yellow", "lantern-corps-ring-sapphire", "lantern-corps-ring-red", "lantern-corps-ring-orange"],
] as const;

const colorSuffix = /-(black|brown|white|red|blue|green|yellow|pink|purple|orange|gray|grey|gold|silver|sapphire|multicolor|bw|surprise)$/i;
const colorNames: Record<string, string> = {
  black: "مشکی", brown: "قهوه‌ای", white: "سفید", red: "قرمز", blue: "آبی",
  green: "سبز", yellow: "زرد", pink: "صورتی", purple: "بنفش", orange: "نارنجی",
  gray: "خاکستری", grey: "خاکستری", gold: "طلایی", silver: "نقره‌ای",
  sapphire: "صورتی", multicolor: "چندرنگ", bw: "سیاه و سفید", surprise: "رنگ شانسی",
};
const namedColors: Record<string, string> = {
  "قلاب-دیواری-گربه-با-فانوس-آویز-سفید-7a352ed4": "سفید",
};

function familyTitle(members: Product[], fallback: string): string {
  const words = members.map((member) => member.name.trim().split(/\s+/));
  const common: string[] = [];
  for (const [index, word] of words[0].entries()) {
    if (!words.every((name) => name[index] === word)) break;
    common.push(word);
  }
  return common.length >= 2 ? common.join(" ") : fallback;
}

/** One public card per color family; the original records stay available to the cart. */
export function groupColorProducts(visible: Product[], all: Product[] = visible): Product[] {
  const allSlugs = new Set(all.map((product) => product.slug));
  const curated = new Map<string, string>(curatedFamilies.flatMap((family) =>
    family.map((slug): [string, string] => [slug, family[0]])));
  const suffixFamilies = new Map<string, number>();
  for (const slug of allSlugs) {
    const match = slug.match(colorSuffix);
    if (match) {
      const base = slug.slice(0, -match[0].length);
      suffixFamilies.set(base, (suffixFamilies.get(base) ?? 0) + 1);
    }
  }
  const familyKey = (slug: string): string | null => {
    const known = curated.get(slug);
    if (known) return known;
    const match = slug.match(colorSuffix);
    const base = match ? slug.slice(0, -match[0].length) : slug;
    const count = (suffixFamilies.get(base) ?? 0) + Number(allSlugs.has(base));
    return count > 1 ? base : null;
  };
  const allFamilies = new Map<string, Product[]>();
  for (const product of all) {
    const key = familyKey(product.slug);
    if (!key) continue;
    const members = allFamilies.get(key) ?? [];
    members.push(product);
    allFamilies.set(key, members);
  }
  const visibleFamilies = new Map<string, Product[]>();
  for (const product of visible) {
    const key = familyKey(product.slug);
    if (!key || (allFamilies.get(key)?.length ?? 0) < 2) continue;
    const members = visibleFamilies.get(key) ?? [];
    members.push(product);
    visibleFamilies.set(key, members);
  }
  const groups = new Map([...visibleFamilies].map(([key, candidates]) => {
    const members = allFamilies.get(key) ?? candidates;
    const representative = [...candidates].sort((a, b) =>
      Number(b.images.length > 0) - Number(a.images.length > 0)
      || b.created_at.localeCompare(a.created_at))[0];
    const colors = members.flatMap((member) => {
      const suffix = member.slug.match(colorSuffix)?.[1]?.toLowerCase();
      const color = namedColors[member.slug] || (suffix ? colorNames[suffix] : "");
      return color ? [color] : [];
    });
    const familyOffers = members.flatMap((member) => {
      const suffix = member.slug.match(colorSuffix)?.[1]?.toLowerCase();
      const color = namedColors[member.slug] || (suffix ? colorNames[suffix] : "");
      return member.variants.map((variant) => ({
        color: color || variant.color,
        price: variant.price,
        stock: variant.stock,
        made_to_order: variant.made_to_order,
      }));
    });
    return [key, {
      ...representative,
      name: familyTitle(members, representative.name),
      images: representative.images.length ? representative.images : members.find((member) => member.images.length)?.images ?? [],
      created_at: members.reduce((newest, member) => member.created_at > newest ? member.created_at : newest, representative.created_at),
      searchTerms: members.flatMap((member) => [member.name, member.slug]),
      availableColors: [...new Set(colors)],
      familyOffers,
      familyCanonicalSlug: members.find((member) => member.slug === key)?.slug ?? members[0].slug,
    } as Product] as [string, Product];
  }));
  const emitted = new Set<string>();
  return visible.flatMap((product) => {
    const key = familyKey(product.slug);
    if (!key || !groups.has(key)) return [product];
    if (emitted.has(key)) return [];
    emitted.add(key);
    return [groups.get(key)!];
  });
}
